/**
 * useMyMarketing — report data for the My Marketing hub, served by the
 * queryMyMarketing callable (us-east1). Overview KPIs and the weekly
 * engagement timeline are held as state; the paged activity log is exposed
 * as an imperative fetcher so the Activity tab can own its pagination.
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functionsUsEast1 } from '../utils/init-firebase';

const queryMyMarketing = httpsCallable(functionsUsEast1, 'queryMyMarketing');

/**
 * @param {string=} viewUserId - Firebase UID to report on. CRM admins may pass
 *   another LO's uid ("view as"); the backend ignores it for non-admins.
 */
export function useMyMarketing(viewUserId) {
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [error, setError] = useState(null);

  const scope = viewUserId ? { userId: viewUserId } : {};

  const loadOverview = useCallback(async (days = 30) => {
    setOverviewLoading(true);
    setError(null);
    try {
      const res = await queryMyMarketing({ reportType: 'overview', days, ...scope });
      setOverview(res.data?.data || null);
    } catch (err) {
      console.error('[useMyMarketing] Overview failed:', err);
      setError(err.message || 'Failed to load marketing overview');
    } finally {
      setOverviewLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewUserId]);

  const loadTimeline = useCallback(async (days = 84) => {
    setTimelineLoading(true);
    try {
      const res = await queryMyMarketing({ reportType: 'engagement_timeline', days, ...scope });
      setTimeline(res.data?.data || null);
    } catch (err) {
      console.error('[useMyMarketing] Timeline failed:', err);
      setError(err.message || 'Failed to load engagement timeline');
    } finally {
      setTimelineLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewUserId]);

  const fetchRecentSends = useCallback(async ({ page = 0, pageSize = 25 } = {}) => {
    const res = await queryMyMarketing({ reportType: 'recent_sends', page, pageSize, ...scope });
    return res.data?.data || { rows: [], total: 0, page, pageSize };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewUserId]);

  return {
    overview,
    overviewLoading,
    timeline,
    timelineLoading,
    error,
    loadOverview,
    loadTimeline,
    fetchRecentSends,
  };
}

export default useMyMarketing;
